import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useApi } from '../../hooks/useApi';
import Loader from '../common/Loader';
import MembersTab from './MembersTab';
import QuotasTab from './QuotasTab';
import GroupsTab from './GroupsTab';
import './Organizations.css';

const formatDate = (val) => {
  if (val == null) return '—';
  const d = new Date(val);
  return isNaN(d.getTime()) ? String(val) : d.toLocaleString();
};

const PLAN_BADGE = { free: 'badge-gray', pro: 'badge-blue', enterprise: 'badge-orange' };

const TAB_SX = {
  '& .MuiTab-root': { color: '#6b7280', textTransform: 'none', fontWeight: 500, fontSize: '0.95rem', minHeight: '48px' },
  '& .MuiTab-root.Mui-selected': { color: '#f97316' },
  '& .MuiTabs-indicator': { backgroundColor: '#f97316', height: '2px' },
};

const OrgDetails = ({ orgId, onBack }) => {
  const { apiCall } = useApi();
  const base = `/admin/organizations/${orgId}`;

  // 204-safe wrapper
  const apiCallNoBody = async (endpoint, options) => {
    try { return await apiCall(endpoint, options); }
    catch (err) { if (err instanceof SyntaxError) return null; throw err; }
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [org, setOrg] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const fetchOrg = async () => {
    setLoading(true); setError('');
    try {
      const data = await apiCall(base);
      setOrg(data.organization || null);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchOrg(); }, [orgId]);

  if (loading) return <Loader />;

  return (
    <div className="orgs-page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button onClick={onBack} className="btn btn-secondary orgs-back-btn">
            <ArrowBackIcon sx={{ fontSize: '1rem' }} /> Back
          </button>
          <h2 className="page-title">{org?.name || 'Organization'}</h2>
        </div>
        <button onClick={fetchOrg} className="btn-icon" disabled={loading}>
          <RefreshIcon className={loading ? 'spinning' : ''} sx={{ fontSize: '1.25rem' }} />
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Organization Info */}
      <div className="settings-card" style={{ marginBottom: '1.5rem' }}>
        <h3 className="card-title">Organization Info</h3>
        <div className="modal-body">
          <div className="detail-row"><span className="detail-label">ID:</span><span className="detail-value detail-value-sm" style={{ fontFamily: 'monospace' }}>{org?.id || '—'}</span></div>
          <div className="detail-row"><span className="detail-label">Name:</span><span className="detail-value">{org?.name || '—'}</span></div>
          <div className="detail-row"><span className="detail-label">Plan:</span><span className="detail-value"><span className={`badge ${PLAN_BADGE[org?.plan] || 'badge-gray'}`}>{org?.plan || '—'}</span></span></div>
          <div className="detail-row"><span className="detail-label">Storage Quota:</span><span className="detail-value">{org?.storage_quota_mb != null ? `${org.storage_quota_mb.toLocaleString()} MB` : '—'}</span></div>
          <div className="detail-row"><span className="detail-label">Members:</span><span className="detail-value">{org?.member_count ?? '—'}</span></div>
          <div className="detail-row"><span className="detail-label">Created:</span><span className="detail-value">{formatDate(org?.created_at)}</span></div>
          <div className="detail-row"><span className="detail-label">Updated:</span><span className="detail-value">{formatDate(org?.updated_at)}</span></div>
        </div>
      </div>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: '#1a1a2e', mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={TAB_SX}>
          <Tab label="Members" />
          <Tab label="Quotas" />
          <Tab label="Groups" />
        </Tabs>
      </Box>

      {activeTab === 0 && <MembersTab orgId={orgId} apiCall={apiCall} apiCallNoBody={apiCallNoBody} />}
      {activeTab === 1 && <QuotasTab orgId={orgId} apiCall={apiCall} apiCallNoBody={apiCallNoBody} />}
      {activeTab === 2 && <GroupsTab orgId={orgId} apiCall={apiCall} apiCallNoBody={apiCallNoBody} />}
    </div>
  );
};

export default OrgDetails;
